// THE one canonical status-to-tone map for the office app (Session AUJ, design system Session 2;
// spec docs/specs/ELE-V2-DESIGN-SYSTEM-COMPONENT-SPEC-2026-10-03.md section 3.3). Before this, 47
// local badge functions and ~50 local style maps each decided what a status looks like, and the
// same status read blue on one screen and amber on the next (a job that was `scheduled`, a
// cancelled visit that fell through to blue, a draft that was purple, grey or blue by page).
//
// TONE RULE, applied to resolve every disagreement so a later session applies it rather than
// re-deciding it:
//   green  = done or good
//   blue   = in hand, or waiting on someone outside the office
//   amber  = needs the office's attention, or at risk
//   red    = failed, overdue, refused, or a legal duty with a clock
//   grey   = closed, neutral, no data
//   purple = ours and not yet sent (a draft)
// A Director-ruled or Director-specified tone (quotes, knowledge ingest, PPM payments) is kept even
// where the rule would read differently.
//
// Existing single sources stay the source and are imported INTO this file, not copied:
// quoteStatusLabel/quoteStatusTone (Director ruling 25/09, Session ARP), ingestStatusPill (Director
// specified), RFID_STATUS_META (labels), STATUS_LABEL in calloutListFilters.ts, the project label
// maps in projectsUi.ts. ppmPaymentPill is DERIVED from several fields, so it stays a function
// there; the registry only converts its answer (ppmPaymentEntry).
//
// An unknown or null status NEVER renders blank and is never dropped: statusEntry returns the
// humanised word on a grey tone (and the pill marks it data-status-unknown).
import { ClipboardCheck, ClockAlert, Hourglass, MailCheck, Package, Pause, Send, Siren, TimerOff, Wrench, type LucideIcon } from "lucide-react";
import type { PillTone } from "../components/StatusPill";
import { holdLabel, effectiveHoldReason, type JobHoldReasonKey } from "./jobHold";
import type {
  AccidentStatus,
  ComplaintSeverity,
  ComplaintStatus,
  EnquiryStatus,
  ProjectStatus,
  ProjectSubcontractorStatus,
  ProjectSurveyStatusView,
  PurchaseOrderStatus,
  ReplyTemplateProposalStatus,
  TimesheetStatus,
} from "./api";
import type { ChaseStatus, ChaseTone } from "./api";
import { quoteStatusLabel, quoteStatusTone, type QuoteStatusValue } from "./quoteStatus";
import { ingestStatusPill, INGEST_STATUS_LABEL, INGEST_STATUSES, type KnowledgeIngestStatus } from "./knowledgeTypes";
import { RFID_STATUS_META, type RfidTagStatus } from "./rfid";
import type { CalloutStatus } from "../routes/office/calloutListFilters";
import { PROJECT_STATUS_LABELS, SUBCONTRACTOR_STATUS_LABELS } from "../routes/office/projectsUi";
import type { PpmPill } from "./ppmPaymentPill";
import type { ExpenseStatus } from "./expenses";

export type PillVariant = "tint" | "solid" | "outline";
export interface StatusEntry {
  label: string;
  tone: PillTone;
  /** Default "tint". */
  variant?: PillVariant;
  /** Session SC9A: the state's ONE icon (lucide-react). The pill shows it in place of the dot and ALWAYS shows the words too. */
  icon?: LucideIcon;
}

/** An entry that carries its icon (Session SC9A). */
const ei = (label: string, tone: PillTone, icon: LucideIcon): StatusEntry => ({ label, tone, icon });

const e =(label: string, tone: PillTone, variant?: PillVariant): StatusEntry =>
  variant ? { label, tone, variant } : { label, tone };

function fromKeys<K extends string>(keys: readonly K[], f: (k: K) => StatusEntry): Record<K, StatusEntry> {
  return Object.fromEntries(keys.map((k) => [k, f(k)])) as Record<K, StatusEntry>;
}

const QUOTE_KEYS: QuoteStatusValue[] = [
  "draft", "pending", "sent", "accepted", "signed", "voided", "expired", "opened", "declined", "lost",
];
const PROJECT_KEYS = Object.keys(PROJECT_STATUS_LABELS) as ProjectStatus[];
const SUBCONTRACTOR_KEYS = Object.keys(SUBCONTRACTOR_STATUS_LABELS) as ProjectSubcontractorStatus[];
const RFID_KEYS = Object.keys(RFID_STATUS_META) as RfidTagStatus[];

const PROJECT_TONE: Record<ProjectStatus, PillTone> = {
  enquiry: "blue", surveying: "blue", quoting: "blue", quoted: "blue", accepted: "blue",
  works_scheduled: "blue", in_progress: "blue", on_hold: "amber", complete: "green",
  invoiced: "green", lost: "red", cancelled: "grey",
};
const SUBCONTRACTOR_TONE: Record<ProjectSubcontractorStatus, PillTone> = {
  proposed: "blue", agreed: "blue", agreement_sent: "blue", agreement_signed: "green",
  on_site: "blue", complete: "green",
};
const RFID_TONE: Record<RfidTagStatus, PillTone> = {
  not_programmed: "grey", url_generated: "amber", sent: "blue", programmed: "green",
};

type ContractStatusValue = "active" | "pending_signature" | "draft" | "expired" | "cancelled";
type InvoiceStatusValue = "overdue" | "paid" | "authorised" | "draft" | "void";
type XeroTemplateStatus = "AUTHORISED" | "DRAFT" | "DELETED" | "MISSING";
type RepairEstimateStatusValue = "draft" | "sent" | "approved" | "declined";
type ProjectQuotationStatus = "signed" | "pending" | "draft" | "none";
type SlaOutcomeValue = "met" | "breached" | "open" | "not_targeted" | "unmeasured";
type EmailOutcomeValue = "pending" | "drafted" | "needs_you" | "no_reply_needed";
type TriageStateValue = "action_owed" | "needs_chase" | "awaiting_reply";
type ReplyTemplateStatusValue = "draft" | "approved" | "retired";
type WorkOrderStatusValue = "pending" | "accepted" | "dismissed";
type DeliverabilityValue = "pass" | "warn" | "fail" | "unknown";
type TrainingStatusValue = "SCHEDULED" | "COMPLETED" | "CANCELLED";
type RiddorValue = "unassessed" | "reportable" | "not_reportable";
type EnforcementValue = "open" | "remedied" | "closed";
type ObjectiveValue = "on_track" | "at_risk" | "achieved" | "missed";
type InternalAuditValue = "open" | "actions_pending" | "closed";
type ManagementReviewValue = "draft" | "finalised";
type CertificateValue = "valid" | "expiring" | "expired" | "no-expiry";
type ReviewDueValue = "overdue" | "due_soon";
type SubcontractorValue = "pending_invite" | "pending_review" | "active" | "inactive";
type SubcontractorAgreementValue = "none" | "sent" | "signed" | "expired";
type SubcontractorTypeValue = "bona_fide" | "labour_only";
type SubcontractorEngagementValue = "bona_fide" | "bona_fide_pending" | "labour_only" | "not_set";
// Session AUZ: a portal upload is "submitted" (the same amber as unverified, in the subcontractor's own words) and the
// office's review can leave it "rejected" (not accepted), which never reads as compliant either.
type DocumentVerificationValue = "unverified" | "verified" | "submitted" | "rejected";
type DefectSeverityValue = "immediate" | "advisory";
type DefectStatusValue = "open" | "resolved";
type LolerCategoryValue = "immediate" | "timed" | "advisory";
// Session BAQ: where an examiner's report item has got to (Insurer items). Amber needs the office, blue is in hand.
type LolerItemStateValue = "raised" | "quoted" | "booked" | "done" | "verified";
type LiftComplianceValue = "green" | "amber" | "red" | "grey";
type ServiceDueValue = "overdue" | "due_soon" | "scheduled" | "no-date";
type CheckResultValue = "pass" | "fail" | "advisory" | "attention" | "na" | "unconfirmed";
type SiteStatusValue = "active" | "potential" | "dead";
type CallOutcomeValue = "complaint" | "follow_up_needed" | "quote_requested" | "resolved" | "other";
// The chase vocabulary (Session AUH, mail/ChasesTab.tsx): the server's four statuses plus the
// office list's own "ended" filter word, and the soft/firm tone ("hard" on the wire, "Firm" on the page).
type ChaseStatusValue = ChaseStatus | "ended";

export const STATUS_REGISTRY = {
  job: {
    scheduled: e("Scheduled", "blue"),
    in_progress: e("In progress", "blue"),
    // Session SC9A: the status parts_pending is screen-worded "On hold" everywhere. With the reason known the pill reads
    // "On hold: <reason>" (primaryStatePill below); this generic entry is the reasonless word.
    parts_pending: ei("On hold", "amber", Pause),
    pending_approval: ei("Closed, awaiting release", "amber", ClipboardCheck),
    complete: e("Complete", "green"),
    cancelled: e("Cancelled", "grey"),
  } satisfies Record<CalloutStatus, StatusEntry>,
  // Session SC9A: the release gate's three labels (BEG, BEK), unchanged words, each with its icon. They were two literals at call sites
  // ("Closed and ready for sending" in ServiceRecordPanel, "Released <date>" in CalloutsPage) and now read from here.
  releaseGate: {
    awaiting_release: ei("Closed, awaiting release", "amber", ClipboardCheck),
    ready_to_send: ei("Closed and ready for sending", "amber", Send),
    sent: ei("Sent", "green", MailCheck),
  } satisfies Record<"awaiting_release" | "ready_to_send" | "sent", StatusEntry>,
  slaOutcome: {
    met: e("Within target", "green"),
    breached: e("Outside target", "red"),
    open: e("Still open", "grey"),
    not_targeted: e("Not targeted", "grey"),
    unmeasured: e("Unmeasured", "grey"),
  } satisfies Record<SlaOutcomeValue, StatusEntry>,
  quote: fromKeys(QUOTE_KEYS, (k) => e(quoteStatusLabel(k), quoteStatusTone(k))),
  projectQuotation: {
    signed: e("Signed", "green"),
    pending: e("Awaiting signature", "blue"),
    draft: e("Draft", "purple"),
    none: e("None yet", "grey"),
  } satisfies Record<ProjectQuotationStatus, StatusEntry>,
  project: fromKeys(PROJECT_KEYS, (k) => e(PROJECT_STATUS_LABELS[k], PROJECT_TONE[k])),
  projectSubcontractor: fromKeys(SUBCONTRACTOR_KEYS, (k) => e(SUBCONTRACTOR_STATUS_LABELS[k], SUBCONTRACTOR_TONE[k])),
  contract: {
    active: e("Active", "green"),
    pending_signature: e("Pending signature", "blue"),
    draft: e("Draft", "purple"),
    expired: e("Expired", "grey"),
    cancelled: e("Cancelled", "grey"),
  } satisfies Record<ContractStatusValue, StatusEntry>,
  invoice: {
    overdue: e("Overdue", "red"),
    paid: e("Paid", "green"),
    authorised: e("Authorised", "blue"),
    draft: e("Draft", "purple"),
    void: e("Void", "grey"),
  } satisfies Record<InvoiceStatusValue, StatusEntry>,
  xeroTemplate: {
    AUTHORISED: e("Active", "green"),
    DRAFT: e("Draft in Xero", "purple"),
    DELETED: e("Deleted in Xero", "red"),
    MISSING: e("Not found in Xero", "red"),
  } satisfies Record<XeroTemplateStatus, StatusEntry>,
  // The flags "Raised on site" (blue) and "Awaiting price" (amber) stay literal pills at their call sites.
  // Review R2 (L3): "sent" is stamped when the email (or, before ARS, Xero) DRAFT is made and a person still sends it, so it reads as drafted.
  repairEstimate: {
    draft: e("Draft", "purple"),
    sent: e("Drafted to customer", "blue"),
    approved: e("Approved", "green"),
    declined: e("Declined", "red"),
  } satisfies Record<RepairEstimateStatusValue, StatusEntry>,
  enquiry: {
    new: e("New", "grey"),
    drafted: e("Drafted", "purple"),
    awaiting_review: e("Awaiting review", "amber"),
    approved: e("Approved", "amber"),
    sent: e("Sent", "green"),
  } satisfies Record<EnquiryStatus, StatusEntry>,
  emailOutcome: {
    pending: e("Pending", "grey"),
    drafted: e("Drafted", "green"),
    needs_you: e("Needs you", "amber"),
    no_reply_needed: e("No reply needed", "grey"),
  } satisfies Record<EmailOutcomeValue, StatusEntry>,
  triageState: {
    action_owed: e("Action owed", "red"),
    needs_chase: e("Needs chase", "amber"),
    awaiting_reply: e("Awaiting reply", "blue"),
  } satisfies Record<TriageStateValue, StatusEntry>,
  replyTemplate: {
    draft: e("Draft", "purple"),
    approved: e("Approved", "green"),
    retired: e("Retired", "grey"),
  } satisfies Record<ReplyTemplateStatusValue, StatusEntry>,
  // The proposal KIND pills keep their current tones at their call sites.
  templateProposal: {
    pending: e("Pending", "amber"),
    accepted: e("Accepted", "green"),
    rejected: e("Rejected", "red"),
  } satisfies Record<ReplyTemplateProposalStatus, StatusEntry>,
  workOrder: {
    pending: e("Waiting", "amber"),
    accepted: e("Raised", "green"),
    dismissed: e("Dismissed", "grey"),
  } satisfies Record<WorkOrderStatusValue, StatusEntry>,
  deliverability: {
    pass: e("Pass", "green"),
    warn: e("Warning", "amber"),
    fail: e("Fail", "red"),
    unknown: e("Unknown", "grey"),
  } satisfies Record<DeliverabilityValue, StatusEntry>,
  timesheet: {
    DRAFT: e("Draft", "purple"),
    SUBMITTED: e("Submitted", "amber"),
    APPROVED: e("Approved", "green"),
  } satisfies Record<TimesheetStatus, StatusEntry>,
  training: {
    SCHEDULED: e("Scheduled", "blue"),
    COMPLETED: e("Completed", "green"),
    CANCELLED: e("Cancelled", "grey"),
  } satisfies Record<TrainingStatusValue, StatusEntry>,
  expense: {
    submitted: e("Submitted", "amber"),
    approved: e("Approved", "green"),
    rejected: e("Rejected", "red"),
  } satisfies Record<ExpenseStatus, StatusEntry>,
  purchaseOrder: {
    draft: e("Draft", "purple"),
    ordered: e("Ordered", "blue"),
    partially_received: e("Partially received", "amber"),
    received: e("Received", "green"),
    cancelled: e("Cancelled", "grey"),
  } satisfies Record<PurchaseOrderStatus, StatusEntry>,
  // Session PJ2F2: a project site survey's status (it had its own map in surveyOfficeUi.ts, the second map the one-map rule forbids).
  projectSurvey: {
    not_started: e("Not started", "grey"),
    in_progress: e("In progress", "blue"),
    submitted: e("Submitted", "amber"),
    reviewed: e("Reviewed", "green"),
    superseded: e("Superseded", "grey"),
  } satisfies Record<ProjectSurveyStatusView, StatusEntry>,
  complaint: {
    open: e("Open", "amber"),
    investigating: e("Investigating", "blue"),
    resolved: e("Resolved", "green"),
    closed: e("Closed", "grey"),
  } satisfies Record<ComplaintStatus, StatusEntry>,
  complaintSeverity: {
    low: e("Low", "grey"),
    medium: e("Medium", "blue"),
    high: e("High", "amber"),
    critical: e("Critical", "red"),
  } satisfies Record<ComplaintSeverity, StatusEntry>,
  accident: {
    open: e("Open", "amber"),
    under_investigation: e("Under investigation", "blue"),
    reported_to_hse: e("Reported to HSE", "blue"),
    closed: e("Closed", "grey"),
  } satisfies Record<AccidentStatus, StatusEntry>,
  // A RIDDOR assessment that has not been made is stored as null: statusEntry maps null to "unassessed".
  riddor: {
    unassessed: e("Not assessed", "amber"),
    reportable: e("Reportable", "red"),
    not_reportable: e("Not reportable", "green"),
  } satisfies Record<RiddorValue, StatusEntry>,
  enforcement: {
    open: e("Open", "amber"),
    remedied: e("Remedied", "green"),
    closed: e("Closed", "grey"),
  } satisfies Record<EnforcementValue, StatusEntry>,
  objective: {
    on_track: e("On track", "green"),
    at_risk: e("At risk", "amber"),
    achieved: e("Achieved", "blue"),
    missed: e("Missed", "red"),
  } satisfies Record<ObjectiveValue, StatusEntry>,
  internalAudit: {
    open: e("Open", "blue"),
    actions_pending: e("Actions pending", "amber"),
    closed: e("Closed", "green"),
  } satisfies Record<InternalAuditValue, StatusEntry>,
  managementReview: {
    draft: e("Draft", "purple"),
    finalised: e("Finalised", "green"),
  } satisfies Record<ManagementReviewValue, StatusEntry>,
  // One domain for engineer certificates, personal documents, subcontractor certificates and the
  // reporting dashboard.
  certificate: {
    valid: e("Valid", "green"),
    expiring: e("Expiring", "amber"),
    expired: e("Expired", "red"),
    "no-expiry": e("No expiry", "grey"),
  } satisfies Record<CertificateValue, StatusEntry>,
  reviewDue: {
    overdue: e("Overdue", "red"),
    due_soon: e("Due soon", "amber"),
  } satisfies Record<ReviewDueValue, StatusEntry>,
  subcontractor: {
    pending_invite: e("Invite pending", "grey"),
    pending_review: e("Pending review", "amber"),
    active: e("Active", "green"),
    inactive: e("Inactive", "grey"),
  } satisfies Record<SubcontractorValue, StatusEntry>,
  // "none" is listed so a caller can look it up, but a subcontractor with no agreement shows NO pill.
  subcontractorAgreement: {
    none: e("No agreement", "grey"),
    sent: e("Sent", "blue"),
    signed: e("Signed", "green"),
    expired: e("Expired", "red"),
  } satisfies Record<SubcontractorAgreementValue, StatusEntry>,
  subcontractorType: {
    bona_fide: e("Bona fide", "blue"),
    labour_only: e("Labour only", "amber"),
  } satisfies Record<SubcontractorTypeValue, StatusEntry>,
  // Session AUR: the office-owned status. Pending (Bona Fide recorded by the sign-up form, not yet approved) and not set
  // both need the office (amber); a set status is in hand (blue for Bona Fide, grey for Labour Only, a neutral fact).
  subcontractorEngagement: {
    bona_fide: e("Bona Fide", "blue"),
    bona_fide_pending: e("Bona Fide, pending approval", "amber"),
    labour_only: e("Labour Only", "grey"),
    not_set: e("Status not set", "amber"),
  } satisfies Record<SubcontractorEngagementValue, StatusEntry>,
  // Session AUR: a document the office has not approved never reads as compliant.
  documentVerification: {
    unverified: e("Unverified", "amber"),
    verified: e("Verified", "green"),
    submitted: e("Submitted, awaiting review", "amber"),
    rejected: e("Not accepted", "red"),
  } satisfies Record<DocumentVerificationValue, StatusEntry>,
  defectSeverity: {
    immediate: e("Immediate (Cat A)", "red", "solid"),
    advisory: e("Advisory (Cat B)", "amber", "solid"),
  } satisfies Record<DefectSeverityValue, StatusEntry>,
  defectStatus: {
    open: e("Open", "blue"),
    resolved: e("Resolved", "green"),
  } satisfies Record<DefectStatusValue, StatusEntry>,
  lolerCategory: {
    immediate: e("Immediate", "red"),
    timed: e("Timed", "amber"),
    advisory: e("Advisory", "grey"),
  } satisfies Record<LolerCategoryValue, StatusEntry>,
  lolerItemState: {
    raised: e("Raised", "amber"),
    quoted: e("Quoted", "blue"),
    booked: e("Booked", "blue"),
    done: e("Done, to verify", "amber"),
    verified: e("Verified", "green"),
  } satisfies Record<LolerItemStateValue, StatusEntry>,
  liftCompliance: {
    green: e("Up to date", "green"),
    amber: e("Due soon", "amber"),
    red: e("Action needed", "red"),
    grey: e("Not known", "grey"),
  } satisfies Record<LiftComplianceValue, StatusEntry>,
  // LastServicedButton and SafedTestsButton share it; SAFed keeps variant "solid" at its call site.
  serviceDue: {
    overdue: e("Overdue", "red"),
    due_soon: e("Due soon", "amber"),
    scheduled: e("Scheduled", "green"),
    "no-date": e("No date", "grey"),
  } satisfies Record<ServiceDueValue, StatusEntry>,
  checkResult: {
    pass: e("Passed", "green"),
    fail: e("Failed", "red"),
    advisory: e("Needs attention", "amber"),
    attention: e("Needs attention", "amber"),
    na: e("Not applicable", "grey"),
    unconfirmed: e("To check", "grey"),
  } satisfies Record<CheckResultValue, StatusEntry>,
  rfid: fromKeys(RFID_KEYS, (k) => e(RFID_STATUS_META[k].label, RFID_TONE[k])),
  // ppmPaymentPill (lib/ppmPaymentPill.ts) is DERIVED from the job status, the payment state and the
  // service-date runway, so it stays a function: convert its answer with ppmPaymentEntry below.
  ppmPayment: {} as Record<string, StatusEntry>,
  knowledgeIngest: fromKeys(INGEST_STATUSES, (k: KnowledgeIngestStatus) => e(INGEST_STATUS_LABEL[k], ingestStatusPill(k))),
  siteStatus: {
    active: e("Active", "green"),
    potential: e("Potential", "blue"),
    dead: e("Dead", "grey"),
  } satisfies Record<SiteStatusValue, StatusEntry>,
  callOutcome: {
    complaint: e("Complaint", "red"),
    follow_up_needed: e("Follow-up needed", "blue"),
    quote_requested: e("Quote requested", "blue"),
    resolved: e("Resolved", "green"),
    other: e("Other", "grey"),
  } satisfies Record<CallOutcomeValue, StatusEntry>,
  // Added by Session AUJ for the Mail module (Session AUH, ChasesTab.tsx / CustomerChaseSection.tsx).
  // An OPEN chase is waiting on someone outside the office (blue); one answered or settled is done
  // (green); one stopped or closed is closed (grey).
  chase: {
    open: e("Open", "blue"),
    satisfied: e("Settled", "green"),
    stopped: e("Stopped", "grey"),
    closed: e("Closed", "grey"),
    ended: e("Ended", "grey"),
  } satisfies Record<ChaseStatusValue, StatusEntry>,
  // The chase tone: "hard" on the wire, "Firm" on the page. Firm is amber because it means the office
  // has a stated reason to press (two soft chases unanswered, a promise broken, a deadline, a lift out
  // of service); soft is the neutral default.
  chaseTone: {
    soft: e("Soft", "grey"),
    hard: e("Firm", "amber"),
  } satisfies Record<ChaseTone, StatusEntry>,
  // Session AVB: a subcontractor's invoice as read from the Xero bill the office entered. Received is waiting on ELE
  // (amber), approved is on its way (blue), paid is done (green); "not available" is never a guess and is neutral grey,
  // never green and never red. The labels are the server's (subcontractorInvoices/invoiceStatus.ts), pinned equal.
  subcontractorInvoice: {
    received: e("Received", "amber"),
    approved: e("Approved", "blue"),
    paid: e("Paid", "green"),
    not_available: e("Status not available", "grey"),
  } satisfies Record<"received" | "approved" | "paid" | "not_available", StatusEntry>,
  // Session RC2: a XERO purchase order to a supply and fit company (Xero's own statuses; not the platform's van parts
  // orders, which are the purchaseOrder domain). A draft is neutral, one awaiting approval needs ELE (amber), an approved
  // order is on its way (blue), a billed one is done (green). DELETED is never shown (the server leaves it out).
  xeroPurchaseOrder: {
    DRAFT: e("Draft", "grey"),
    SUBMITTED: e("Awaiting approval", "amber"),
    AUTHORISED: e("Approved", "blue"),
    BILLED: e("Billed", "green"),
  } satisfies Record<"DRAFT" | "SUBMITTED" | "AUTHORISED" | "BILLED", StatusEntry>,
} as const;

export type StatusDomain = keyof typeof STATUS_REGISTRY;

/** "parts_pending" -> "Parts pending"; "no-expiry" -> "No expiry". */
export function humanise(status: string): string {
  const words = status.replace(/[_-]+/g, " ").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

// A null status in a domain where null is a real, named state.
const NULL_KEY: Partial<Record<StatusDomain, string>> = { riddor: "unassessed", subcontractorEngagement: "not_set" };

export function statusEntry(domain: StatusDomain, status: string | null | undefined): StatusEntry {
  const table = STATUS_REGISTRY[domain] as Record<string, StatusEntry>;
  const key = status == null || status === "" ? NULL_KEY[domain] : status;
  if (key != null && Object.prototype.hasOwnProperty.call(table, key)) return table[key];
  const word = status == null ? "" : humanise(String(status));
  return { label: word || "Unknown", tone: "grey" };
}

/** True when the registry holds the status (so a pill can mark an unknown one). */
export function isKnownStatus(domain: StatusDomain, status: string | null | undefined): boolean {
  const table = STATUS_REGISTRY[domain] as Record<string, StatusEntry>;
  const key = status == null || status === "" ? NULL_KEY[domain] : status;
  return key != null && Object.prototype.hasOwnProperty.call(table, key);
}

/** The PPM payment pill's answer as a registry entry: outline means "office action needed". */
export function ppmPaymentEntry(pill: PpmPill): StatusEntry {
  return e(pill.label, pill.tone, pill.outline ? "outline" : "tint");
}

// ---- ONE LOOK FOR EVERY STATE (Session SC9A, Director 22 October 2026) -------------------------------------------------------------
//
// A job, call-out or visit can be in several states at once (an entrapment that is on hold, a held visit that is also overdue). A row shows
// ONE state pill, the most urgent, in this fixed order, written once, here: entrapment, overdue, on hold, SLA held, follow-on works,
// awaiting approval, then the rest (the ordinary status pill, which includes the release-gate labels). NEVER a row of badges.
// Each state has one icon and ALWAYS the words, so colour is never the only signal. Tones follow the tone rule at the top of this file.
//
//   Entrapment                  red    Siren       (always first)
//   Overdue                     red    ClockAlert
//   On hold: <reason>           amber  Pause       (Awaiting parts shows Package instead)
//   SLA held: <reason>          blue   TimerOff    (lucide has no clock-pause; a clock with a bar through it reads as a paused clock)
//   Follow-on works             amber  Wrench
//   Awaiting customer approval  blue   Hourglass   (a repair estimate sent and not answered)
//   Closed, awaiting release / Closed and ready for sending / Sent   see the releaseGate domain above.
export const STATE_KINDS = ["entrapment", "overdue", "on_hold", "sla_held", "follow_on", "awaiting_approval"] as const;
export type StateKind = (typeof STATE_KINDS)[number];
/** The fixed urgency order of the state pills. A row's ONE pill is the first kind in this list that applies. */
export const STATE_PILL_ORDER: readonly StateKind[] = STATE_KINDS;

export const STATE_LOOK: Record<StateKind, { label: string; tone: PillTone; icon: LucideIcon }> = {
  entrapment: { label: "Entrapment", tone: "red", icon: Siren },
  overdue: { label: "Overdue", tone: "red", icon: ClockAlert },
  on_hold: { label: "On hold", tone: "amber", icon: Pause },
  sla_held: { label: "SLA held", tone: "blue", icon: TimerOff },
  follow_on: { label: "Follow-on works", tone: "amber", icon: Wrench },
  awaiting_approval: { label: "Awaiting customer approval", tone: "blue", icon: Hourglass },
};

export interface StatePillSpec {
  kind: StateKind;
  word: string;
  tone: PillTone;
  icon: LucideIcon;
}

/** One state as a pill spec. `detail` is the words after the colon ("On hold: awaiting parts"). */
export function statePill(kind: StateKind, detail?: string | null): StatePillSpec {
  const look = STATE_LOOK[kind];
  return { kind, word: detail ? `${look.label}: ${detail}` : look.label, tone: look.tone, icon: look.icon };
}

/** The facts a row's state pill is chosen from. Every one is optional: a row that does not know a fact never shows that state. */
export interface StateFacts {
  entrapment?: boolean | null;
  /** The visit is booked in the past and not finished (see isJobOverdue). */
  overdue?: boolean | null;
  /** The job's status; parts_pending means on hold. */
  status?: string | null;
  /** Why it is on hold (null on an older pause = awaiting parts). The office's own words for Other come in holdReasonText. */
  holdReason?: JobHoldReasonKey | string | null;
  holdReasonText?: string | null;
  /** "SLA held: awaiting customer payment" while the call-out's response clock is paused (the server's own label). */
  slaHeldLabel?: string | null;
  followOnWorks?: boolean | null;
  /** A repair estimate has been sent and not answered. */
  awaitingApproval?: boolean | null;
}

/**
 * THE ONE STATE PILL for a row: the most urgent state that applies, in STATE_PILL_ORDER, or null when none does (the caller then shows the
 * ordinary status pill, "the rest"). A row never shows more than this one. Pure.
 */
export function primaryStatePill(f: StateFacts): StatePillSpec | null {
  for (const kind of STATE_PILL_ORDER) {
    switch (kind) {
      case "entrapment":
        if (f.entrapment === true) return statePill("entrapment");
        break;
      case "overdue":
        // D-SC9A-1 (fix round 1): a job ON HOLD is not overdue FOR THE PILL, so the held reason shows (a held job's booked day has nearly always
        // passed and "Overdue" hid why it was waiting). isJobOverdue and every count are unchanged. ONE-LINE REVERT: delete the status test.
        if (f.overdue === true && f.status !== "parts_pending") return statePill("overdue");
        break;
      case "on_hold":
        if (f.status === "parts_pending") {
          const reason = effectiveHoldReason((f.holdReason ?? null) as JobHoldReasonKey | null);
          const spec = statePill("on_hold", holdLabel(reason, f.holdReasonText).slice("On hold: ".length));
          // Awaiting parts shows the parts icon.
          return reason === "awaiting_parts" ? { ...spec, icon: Package } : spec;
        }
        break;
      case "sla_held":
        if (f.slaHeldLabel) return statePill("sla_held", f.slaHeldLabel.replace(/^SLA held:\s*/i, "") || null);
        break;
      case "follow_on":
        if (f.followOnWorks === true) return statePill("follow_on");
        break;
      case "awaiting_approval":
        if (f.awaitingApproval === true) return statePill("awaiting_approval");
        break;
    }
  }
  return null;
}

/**
 * A job is OVERDUE when it is still open work (scheduled, in progress or on hold: the Scheduling page's own late rule, REASSIGNABLE) and its day has passed. Overdue outranks on hold in the fixed order (the Director's, 22 October 2026), EXCEPT that a held job's pill reads On hold (D-SC9A-1, primaryStatePill); this function and all counts still include it. `today` is the
 * London day (YYYY-MM-DD) and `scheduledDay` the job's London day. Pure; the caller supplies both so it never reads a clock itself.
 */
export function isJobOverdue(status: string | null | undefined, scheduledDay: string | null | undefined, today: string): boolean {
  if (status !== "scheduled" && status !== "in_progress" && status !== "parts_pending") return false;
  return Boolean(scheduledDay) && (scheduledDay as string) < today;
}

/** Follow-on works links to the faults raised from that visit, in the To fix queue (the Defects page's jobId filter). */
export function followOnWorksHref(jobId: string): string {
  return `/office/defects?status=open&jobId=${encodeURIComponent(jobId)}`;
}
